import type { FC, ReactNode } from 'react'
import type {
    DayAvailability,
    ServiceId,
    SessionId,
    SlotAvailability
} from '@/types'

import { useState } from 'react'
import { ArrowRight, CalendarPlus, FlaskConical } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isBookable } from '@/lib/availability'
import { SectionHeading } from '@/components/generals/section-heading'
import { slotStatusMeta } from '@/components/landing/slot-status'
import { BookButton, useBooking } from '@/components/landing/booking-provider'

import {
    formatDateRange,
    formatDayLong,
    formatDayShort,
    MONTH_SHORT,
    parseIsoDate,
    WEEKDAY_SHORT
} from '@/lib/dates'

import {
    AVAILABILITY_PREVIEW_DAYS,
    serviceOrder,
    services,
    sessionOrder,
    sessions
} from '@/constants/clinic'

const bookingSteps = [
    {
        title: 'Pilih layanan dan jadwal',
        text: 'Lihat kuota yang masih ada, lalu pilih sesi pagi atau sore.'
    },
    {
        title: 'Isi data pasien',
        text: 'Cukup nama dan nomor WhatsApp. Tidak perlu membuat akun.'
    },
    {
        title: 'Datang sesuai sesi',
        text: 'Tunjukkan kode janji saat mendaftar, bawa KTP dan kartu JKN.'
    }
]

interface SlotPillProps {
    date: string
    service: ServiceId
    session: SessionId
    slot: SlotAvailability
    block?: boolean
}

const SlotPill: FC<SlotPillProps> = ({
    date,
    service,
    session,
    slot,
    block
}): ReactNode => {
    const booking = useBooking()
    const meta = slotStatusMeta[slot.status]

    const content = (
        <>
            <span className={cn('size-1.5 shrink-0 rounded-full', meta.dot)} aria-hidden='true' />
            <span className='whitespace-nowrap'>
                {sessions[session].label}
                {' · '}
                {meta.label}
            </span>
        </>
    )

    const base = cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset',
        meta.badge,
        block && 'w-full justify-center'
    )

    if (!booking || !isBookable(slot)) {
        return (
            <span className={cn(base, 'cursor-default')}>
                {content}
            </span>
        )
    }

    return (
        <button
            type='button'
            onClick={() => booking.openBooking({ service, date, session })}
            aria-label={`Buat janji ${services[service].name}, ${formatDayLong(date)}, ${sessions[session].name.toLowerCase()}, ${meta.label.toLowerCase()}`}
            className={cn(
                base,
                'group/slot transition-all outline-none hover:shadow-md hover:ring-2 focus-visible:ring-4 focus-visible:ring-ring/30'
            )}
        >
            {content}
            <ArrowRight className='-mr-0.5 size-3 shrink-0 opacity-50 transition-all group-hover/slot:translate-x-0.5 group-hover/slot:opacity-100' aria-hidden='true' />
        </button>
    )
}

const StatusLegend: FC = (): ReactNode => {
    return (
        <ul className='flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-soft' aria-label='Keterangan status'>
            {(['tersedia', 'hampir-penuh', 'penuh', 'tidak-praktik'] as const).map(status => (
                <li key={status} className='inline-flex items-center gap-2'>
                    <span className={cn('size-2 rounded-full', slotStatusMeta[status].dot)} aria-hidden='true' />
                    {slotStatusMeta[status].label}
                </li>
            ))}
        </ul>
    )
}

const ServiceLabel: FC<{ id: ServiceId }> = ({ id }): ReactNode => {
    const Icon = services[id].icon

    return (
        <span className='flex items-center gap-3'>
            <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-100'>
                <Icon className='size-5' aria-hidden='true' />
            </span>

            <span className='font-bold text-ink'>
                {services[id].name}
            </span>
        </span>
    )
}

const AvailabilitySection: FC = (): ReactNode => {
    const booking = useBooking()
    const days: DayAvailability[] = (booking?.days ?? []).slice(0, AVAILABILITY_PREVIEW_DAYS)
    const [activeIndex, setActiveIndex] = useState(0)
    const activeDay = days[Math.min(activeIndex, Math.max(days.length - 1, 0))]

    const period = days.length > 0
        ? formatDateRange(days[0].date, days[days.length - 1].date)
        : null

    return (
        <section id='ketersediaan' aria-labelledby='ketersediaan-title' className='py-20 sm:py-24'>
            <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                <div className='flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='ketersediaan-title'
                        eyebrow='Janji online'
                        title='Ketersediaan janji'
                        badge={(
                            <span className='inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold tracking-wider text-slate-600 uppercase ring-1 ring-slate-200 ring-inset'>
                                <FlaskConical className='size-3.5' aria-hidden='true' />
                                Data demo
                            </span>
                        )}
                        description={(
                            <>
                                {period && (
                                    <>
                                        Kuota
                                        {' '}
                                        <span className='font-semibold text-ink'>
                                            {period}
                                        </span>
                                        .
                                        {' '}
                                    </>
                                )}
                                Status diperbarui otomatis setiap ada janji baru. Hari Minggu tidak tersedia untuk janji poli.
                            </>
                        )}
                    />

                    <StatusLegend />
                </div>

                {days.length > 0 ? (
                    <>
                        <div className='mt-10 hidden overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-border md:block'>
                            <table className='w-full border-collapse text-sm'>
                                <caption className='sr-only'>
                                    Ketersediaan kuota janji per layanan dan sesi
                                </caption>

                                <thead className='bg-muted/70'>
                                    <tr>
                                        <th scope='col' className='px-6 py-4 text-left font-semibold text-muted-foreground'>
                                            Layanan
                                        </th>

                                        {days.map(day => (
                                            <th key={day.date} scope='col' className='px-4 py-4 text-left font-semibold text-ink'>
                                                {formatDayShort(day.date)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody className='divide-y divide-border'>
                                    {serviceOrder.map(service => (
                                        <tr key={service} className='transition-colors hover:bg-muted/30'>
                                            <th scope='row' className='px-6 py-5 text-left font-normal'>
                                                <ServiceLabel id={service} />
                                            </th>

                                            {days.map(day => (
                                                <td key={day.date} className='px-4 py-4 align-middle'>
                                                    <div className='flex flex-col items-start gap-2'>
                                                        {sessionOrder.map(session => (
                                                            <SlotPill
                                                                key={session}
                                                                date={day.date}
                                                                service={service}
                                                                session={session}
                                                                slot={day.slots[service][session]}
                                                            />
                                                        ))}
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className='mt-8 md:hidden'>
                            <div className='grid grid-cols-4 gap-2' role='group' aria-label='Pilih tanggal'>
                                {days.map((day, index) => {
                                    const parsed = parseIsoDate(day.date)
                                    const selected = activeDay?.date === day.date

                                    if (!parsed) return null

                                    return (
                                        <button
                                            key={day.date}
                                            type='button'
                                            aria-pressed={selected}
                                            onClick={() => setActiveIndex(index)}
                                            className={cn(
                                                'flex flex-col items-center rounded-2xl border px-1 py-2.5 transition-all outline-none focus-visible:ring-4 focus-visible:ring-ring/30',
                                                selected
                                                    ? 'border-brand-600 bg-brand-600 text-white shadow-md shadow-brand-700/25'
                                                    : 'border-border bg-white text-ink'
                                            )}
                                        >
                                            <span className='text-xs font-medium opacity-80'>
                                                {WEEKDAY_SHORT[parsed.getUTCDay()]}
                                            </span>

                                            <span className='text-lg leading-tight font-bold tabular-nums'>
                                                {parsed.getUTCDate()}
                                            </span>

                                            <span className='text-xs opacity-80'>
                                                {MONTH_SHORT[parsed.getUTCMonth()]}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>

                            {activeDay && (
                                <div className='mt-4 grid gap-3'>
                                    {serviceOrder.map(service => (
                                        <div key={service} className='rounded-3xl bg-white p-4 shadow-sm ring-1 ring-border'>
                                            <ServiceLabel id={service} />

                                            <div className='mt-4 grid grid-cols-2 gap-2'>
                                                {sessionOrder.map(session => (
                                                    <SlotPill
                                                        key={session}
                                                        date={activeDay.date}
                                                        service={service}
                                                        session={session}
                                                        slot={activeDay.slots[service][session]}
                                                        block
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <p className='mt-10 rounded-3xl bg-white p-6 text-ink-soft ring-1 ring-border'>
                        Data kuota sedang tidak dapat dimuat. Silakan hubungi klinik untuk membuat janji.
                    </p>
                )}

                <div className='relative mt-10 overflow-hidden rounded-3xl bg-brand-900 p-6 text-white sm:p-8 lg:p-10'>
                    <div className='pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-brand-500/30 blur-3xl' aria-hidden='true' />

                    <div className='relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-12'>
                        <ol className='grid gap-6 sm:grid-cols-3'>
                            {bookingSteps.map((step, index) => (
                                <li key={step.title} className='flex gap-3.5'>
                                    <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm font-bold text-brand-100 ring-1 ring-white/20'>
                                        {index + 1}
                                    </span>

                                    <div>
                                        <p className='font-semibold'>
                                            {step.title}
                                        </p>

                                        <p className='mt-1 text-sm leading-relaxed text-white/70'>
                                            {step.text}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <div className='flex flex-col items-start gap-2 lg:items-end'>
                            <BookButton variant='inverse' size='lg'>
                                <CalendarPlus className='size-5' aria-hidden='true' />
                                Buat Janji
                            </BookButton>

                            <p className='text-xs text-white/60'>
                                Tanpa membuat akun.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default AvailabilitySection
